import classNames from "classnames";
import React from "react";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { WebPageURL } from "tsi-common-react/src/models/nominals";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import {
    FinancingPlanMeta,
    getDefaultFinancingPlan,
} from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import {
    CAPTION_MEDIUM_CLASS_NAME,
    LINK_CLASS_NAME,
} from "tsi-common-react/src/utils/typography";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

const COCOON_CREDIT_CARD_PATH = "img/finance/cocoon-credit-card.png";

const useStaticURL = (): string => {
    const context = React.useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

const renderTermsContainer = (
    plan: FinancingPlanMeta | null,
    financeLink: WebPageURL | null,
): React.ReactNode => {
    const preApprovalContainerClasses = classNames({
        [styles.container]: true,
        [styles.containerText]: true,
        [styles.containerThreeCol]: plan?.description,
    });
    if (!plan?.description) {
        return null;
    }

    const aprInfoURL = `${financeLink}#apr-information`;
    const minPurchase = format.roundedMoney(plan.threshold);

    return (
        <div className={preApprovalContainerClasses}>
            <div>
                <h3>
                    {plan.description}
                    <sup>
                        <a href={aprInfoURL}>
                            {plan.superscript}
                            <span className="ada-screenreader-only">
                                {t`Additional information about Terms and Conditions`}
                            </span>
                        </a>
                    </sup>
                </h3>
                <p
                    className={concatClassNames([
                        CAPTION_MEDIUM_CLASS_NAME,
                        styles.containerCaption,
                    ])}
                >
                    {t`with equal monthly payments & minimum purchase of ${minPurchase}`}
                </p>
            </div>
            <div className={styles.containerCta}>
                <FinancingModalTrigger
                    className="button al-PLCC_Modal-fullapp"
                    title={t`Apply Now`}
                    modalType="full-app"
                >
                    {t`Apply Now`}
                </FinancingModalTrigger>
            </div>
        </div>
    );
};

export const FinancingModalTriggerBlock = (): React.ReactNode => {
    const [plan, setPlan] = React.useState<FinancingPlanMeta | null>(null);
    const financeLink = usePageURL("finance_link");
    const staticURL = useStaticURL();

    React.useEffect(() => {
        const defaultPlan = getDefaultFinancingPlan();
        if (defaultPlan) {
            setPlan(defaultPlan);
        }
    }, []);

    const preApprovalContainerClasses = classNames({
        [styles.container]: true,
        [styles.containerText]: true,
        [styles.containerThreeCol]: plan?.description,
    });
    const preApprovalContainerClassesImage = classNames({
        [styles.container]: true,
        [styles.containerImage]: true,
        [styles.containerThreeCol]: plan?.description,
    });

    return (
        <section className={styles.root}>
            <h2 className={styles.rootTitle}>{t`BUY NOW, PAY OVER TIME`}</h2>
            <div className={`l-capped-width ${styles.rootWrapper}`}>
                <div className={preApprovalContainerClasses}>
                    <div>
                        <h3>{t`Check if you pre-qualify`}</h3>
                        <p
                            className={concatClassNames([
                                CAPTION_MEDIUM_CLASS_NAME,
                                styles.containerCaption,
                            ])}
                        >
                            {t`without affecting your credit score`}
                        </p>
                    </div>
                    <div className={styles.containerCta}>
                        <FinancingModalTrigger
                            className="button al-PLCC_Modal-prequal"
                            title={t`Pre-Qualify`}
                            modalType="prequal-app"
                        >
                            {t`Check Now`}
                        </FinancingModalTrigger>
                    </div>
                </div>
                <div className={preApprovalContainerClassesImage}>
                    <img
                        className="responsive-img"
                        src={`${staticURL}${COCOON_CREDIT_CARD_PATH}`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <Link className={LINK_CLASS_NAME} href={financeLink}>
                        {t`learn more`}
                    </Link>
                </div>
                {renderTermsContainer(plan, financeLink)}
            </div>
        </section>
    );
};
